import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

/**
 * PlaylistScheduleIndicator - אינדיקטור לוח זמנים של פלייליסט
 * מציג מתי הפלייליסט פעיל במהלך השבוע
 */
const PlaylistScheduleIndicator = ({ schedule, playlistId, compact = false }) => {
    // אם אין schedule או playlistId - לא מציגים כלום
    if (!schedule || !playlistId) {
        return null;
    }

    // מציאת כל הימים והשעות שבהם הפלייליסט פעיל
    const playlistSchedule = getPlaylistSchedule(schedule, playlistId);

    // אם הפלייליסט לא משובץ בכלל
    if (!playlistSchedule.isScheduled) {
        return (
            <Chip
                label="לא משובץ"
                size="small"
                sx={{
                    bgcolor: '#f5f5f5',
                    color: '#999',
                    fontSize: '0.75rem',
                    height: '24px'
                }}
            />
        );
    }

    // בדיקה אם הפלייליסט פעיל כרגע
    const isActiveNow = checkIfActiveNow(playlistSchedule);

    // אם הפלייליסט פעיל כל השבוע כל היום
    if (playlistSchedule.isFullWeek && playlistSchedule.isAllDay) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isActiveNow && (
                    <Chip
                        label="• פעיל כעת"
                        size="small"
                        sx={{
                            bgcolor: '#10b981',
                            color: 'white',
                            fontSize: '0.75rem',
                            height: '24px',
                            fontWeight: 'bold'
                        }}
                    />
                )}
                <Chip
                    label="כל השבוע"
                    size="small"
                    sx={{
                        bgcolor: 'rgba(30,58,95,0.07)',
                        color: '#1e3a5f',
                        border: '1px solid rgba(30,58,95,0.15)',
                        fontSize: '0.75rem',
                        height: '24px',
                        fontWeight: 500
                    }}
                />
                <Chip
                    label="כל היום"
                    size="small"
                    sx={{
                        bgcolor: '#f3e5f5',
                        color: '#7b1fa2',
                        fontSize: '0.75rem',
                        height: '24px'
                    }}
                />
            </Box>
        );
    }

    // תצוגה רגילה - ימים + שעות
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* סטטוס פעיל כעת */}
            {isActiveNow && (
                <Chip
                    label="• פעיל כעת"
                    size="small"
                    sx={{
                        bgcolor: '#10b981',
                        color: 'white',
                        fontSize: '0.75rem',
                        height: '24px',
                        fontWeight: 700,
                        boxShadow: '0 0 8px rgba(16,185,129,0.35)'
                    }}
                />
            )}

            {/* ימים */}
            <Chip
                icon={<CalendarTodayIcon sx={{ fontSize: '0.9rem !important' }} />}
                label={playlistSchedule.daysText}
                size="small"
                sx={{
                    bgcolor: '#e3f2fd',
                    color: '#1e3a5f',
                    fontSize: '0.75rem',
                    height: '24px',
                    '& .MuiChip-icon': {
                        marginRight: '4px',
                        marginLeft: 0
                    }
                }}
            />

            {/* שעות */}
            <Tooltip title={playlistSchedule.hoursTooltip} placement="top">
                <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: '0.9rem !important' }} />}
                    label={playlistSchedule.hoursText}
                    size="small"
                    sx={{
                        bgcolor: 'rgba(154,114,9,0.07)',
                        color: '#7a5a07',
                        border: '1px solid rgba(154,114,9,0.18)',
                        fontSize: '0.75rem',
                        height: '24px',
                        fontWeight: 500,
                        '& .MuiChip-icon': {
                            marginRight: '4px',
                            marginLeft: 0
                        }
                    }}
                />
            </Tooltip>

            {/* מיני-לוח שבועי */}
            {!compact && (
                <WeeklyMiniGrid schedule={playlistSchedule.days} />
            )}
        </Box>
    );
};

/**
 * WeeklyMiniGrid - תצוגת מיני-לוח שבועי
 */
const WeeklyMiniGrid = ({ schedule }) => {
    const dayNames = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

    return (
        <Tooltip
            title={
                <Box sx={{ p: 0.5 }}>
                    {schedule.map((day, index) => (
                        <Typography key={index} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                            {getDayName(index)}: {day.active ? day.times.join(', ') : 'לא פעיל'}
                        </Typography>
                    ))}
                </Box>
            }
            placement="top"
        >
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {schedule.map((day, index) => (
                    <Box
                        key={index}
                        sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '4px',
                            background: day.active ? 'linear-gradient(135deg, #1a3352 0%, #1e3a5f 100%)' : 'rgba(30,58,95,0.07)',
                            boxShadow: day.active ? '0 1px 4px rgba(30,58,95,0.3)' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.65rem',
                            color: day.active ? 'white' : '#9ca3af',
                            fontWeight: day.active ? 700 : 400,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'scale(1.15)',
                                boxShadow: day.active ? '0 2px 8px rgba(30,58,95,0.4)' : '0 1px 4px rgba(0,0,0,0.1)'
                            }
                        }}
                    >
                        {dayNames[index]}
                    </Box>
                ))}
            </Box>
        </Tooltip>
    );
};

/**
 * פונקציות עזר
 */

// מציאת כל הימים והשעות שבהם הפלייליסט פעיל
function getPlaylistSchedule(schedule, playlistId) {
    const days = [];
    let totalSlots = 0;

    // עבור על כל ימי השבוע (0-6)
    for (let day = 0; day < 7; day++) {
        const daySchedule = schedule[day] || [];
        const daySlots = daySchedule.filter(slot => slot.playlistId === playlistId);

        if (daySlots.length > 0) {
            totalSlots += daySlots.length;
            days.push({
                day,
                active: true,
                times: daySlots.map(slot => `${slot.startTime}-${slot.endTime}`)
            });
        } else {
            days.push({
                day,
                active: false,
                times: []
            });
        }
    }

    // אם לא משובץ בכלל
    if (totalSlots === 0) {
        return { isScheduled: false };
    }

    // בדיקה אם פעיל כל השבוע
    const activeDays = days.filter(d => d.active);
    const isFullWeek = activeDays.length === 7;

    // בדיקה אם פעיל כל היום (00:00-23:59 או דומה)
    const isAllDay = activeDays.every(d =>
        d.times.some(time => {
            const [start, end] = time.split('-');
            return start === '00:00' && (end === '23:59' || end === '24:00');
        })
    );

    // בניית טקסט ימים
    const daysText = buildDaysText(activeDays);

    // בניית טקסט שעות
    const { hoursText, hoursTooltip } = buildHoursText(activeDays);

    return {
        isScheduled: true,
        isFullWeek,
        isAllDay,
        days,
        daysText,
        hoursText,
        hoursTooltip
    };
}

// בניית טקסט ימים (א'-ה', שישי-שבת, וכו')
function buildDaysText(activeDays) {
    if (activeDays.length === 0) return '';
    if (activeDays.length === 7) return 'כל השבוע';

    const dayNumbers = activeDays.map(d => d.day).sort((a, b) => a - b);

    // בדיקה אם זה טווח רציף
    const isConsecutive = dayNumbers.every((day, index) => {
        if (index === 0) return true;
        return day === dayNumbers[index - 1] + 1;
    });

    if (isConsecutive && dayNumbers.length > 2) {
        const start = getDayNameShort(dayNumbers[0]);
        const end = getDayNameShort(dayNumbers[dayNumbers.length - 1]);
        return `${start}-${end}`;
    }

    // אחרת - רשימת ימים
    if (dayNumbers.length <= 3) {
        return dayNumbers.map(d => getDayNameShort(d)).join(', ');
    }

    return `${dayNumbers.length} ימים`;
}

// בניית טקסט שעות
function buildHoursText(activeDays) {
    if (activeDays.length === 0) return { hoursText: '', hoursTooltip: '' };

    // בדיקה אם כל הימים הפעילים עם אותן שעות
    const firstDayTimes = activeDays[0].times.join(', ');
    const allSameTimes = activeDays.every(d => d.times.join(', ') === firstDayTimes);

    if (allSameTimes && activeDays[0].times.length === 1) {
        // כל הימים עם אותו טווח שעות אחד
        const daysList = activeDays.map(d => getDayName(d.day)).join(', ');
        return {
            hoursText: activeDays[0].times[0],
            hoursTooltip: `${daysList}: ${activeDays[0].times[0]}`
        };
    }

    if (allSameTimes && activeDays[0].times.length > 1) {
        // כל הימים עם מספר טווחי שעות זהים
        const daysList = activeDays.map(d => getDayName(d.day)).join(', ');
        return {
            hoursText: `${activeDays[0].times.length} טווחים`,
            hoursTooltip: `${daysList}:\n${activeDays[0].times.join('\n')}`
        };
    }

    // שעות משתנות בין הימים
    const tooltip = activeDays
        .map(d => `${getDayName(d.day)}: ${d.times.join(', ')}`)
        .join('\n');

    return {
        hoursText: 'זמנים משתנים',
        hoursTooltip: tooltip
    };
}

// בדיקה אם הפלייליסט פעיל כרגע
function checkIfActiveNow(playlistSchedule) {
    if (!playlistSchedule.isScheduled) return false;

    const now = new Date();
    const currentDay = now.getDay(); // 0 = ראשון, 6 = שבת
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    const todaySchedule = playlistSchedule.days[currentDay];
    if (!todaySchedule || !todaySchedule.active) return false;

    // בדיקה אם השעה הנוכחית בתוך אחד מטווחי הזמן
    return todaySchedule.times.some(timeRange => {
        const [start, end] = timeRange.split('-');
        return currentTime >= start && currentTime <= end;
    });
}

// שמות ימים
function getDayName(day) {
    const names = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    return names[day];
}

function getDayNameShort(day) {
    const names = ['א\'', 'ב\'', 'ג\'', 'ד\'', 'ה\'', 'ו\'', 'ש\''];
    return names[day];
}

export default PlaylistScheduleIndicator;
